/**
 * Export Utilities
 * Functions to export data to Excel, CSV, PDF formats, and charts as images.
 * Includes template download functionality for bulk imports.
 * Handles date formatting, number formatting, and Arabic text in exports.
 */

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { RefObject } from 'react';

// ---------------------------------------------------------------------------
// Type definitions
// ---------------------------------------------------------------------------

/**
 * Column definition for exports.
 * Matches the task specification's ColumnDef type.
 */
export interface ColumnDef {
  /** Data key in the source object */
  key: string;
  /** Display header in the exported file */
  header: string;
  /** Optional column width (in characters for Excel, mm for PDF) */
  width?: number;
  /** Optional value formatter */
  format?: (val: any) => string;
}

/**
 * Legacy ExportColumn interface retained for backwards compatibility
 * with existing ExportButton component.
 */
export interface ExportColumn {
  key: string;
  label: string;
  format?: (value: any) => string;
}

export interface ExportOptions {
  filename: string;
  sheetName?: string;
  title?: string;
  subtitle?: string;
}

// ---------------------------------------------------------------------------
// Utility: convert ColumnDef[] to ExportColumn[] for backwards compat
// ---------------------------------------------------------------------------

const toExportColumns = (columns: ColumnDef[]): ExportColumn[] =>
  columns.map((c) => ({ key: c.key, label: c.header, format: c.format }));

// ---------------------------------------------------------------------------
// Utility: detect Arabic / RTL text
// ---------------------------------------------------------------------------

const ARABIC_REGEX = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * Returns true if the string contains Arabic characters.
 */
export const containsArabic = (text: string): boolean => {
  return ARABIC_REGEX.test(text);
};

/**
 * Wraps Arabic text with Unicode RTL mark for correct display in exports.
 */
const wrapRTL = (text: string): string => {
  if (containsArabic(text)) {
    return `\u200F${text}\u200F`;
  }
  return text;
};

// ---------------------------------------------------------------------------
// Template definitions for bulk import
// ---------------------------------------------------------------------------

export interface TemplateDefinition {
  sheetName: string;
  headers: string[];
  sampleRow?: Record<string, any>;
}

const templateDefinitions: Record<string, TemplateDefinition> = {
  inventory: {
    sheetName: 'Inventory',
    headers: [
      'SKU', 'Product Name', 'Store Code', 'Quantity On Hand',
      'Reorder Point', 'Safety Stock', 'Unit Cost', 'Location',
    ],
    sampleRow: {
      'SKU': 'SKU-001',
      'Product Name': 'Luxury Serum 30ml',
      'Store Code': 'STORE-CAS-01',
      'Quantity On Hand': 150,
      'Reorder Point': 50,
      'Safety Stock': 20,
      'Unit Cost': 45.00,
      'Location': 'Morocco Mall',
    },
  },
  products: {
    sheetName: 'Products',
    headers: [
      'SKU', 'Product Name', 'Brand', 'Category', 'Subcategory',
      'Unit Cost', 'Retail Price', 'Weight (g)', 'Status',
    ],
    sampleRow: {
      'SKU': 'SKU-001',
      'Product Name': 'Luxury Serum 30ml',
      'Brand': 'Dior',
      'Category': 'Skincare',
      'Subcategory': 'Serums',
      'Unit Cost': 45.00,
      'Retail Price': 120.00,
      'Weight (g)': 50,
      'Status': 'Active',
    },
  },
  sales: {
    sheetName: 'Sales',
    headers: [
      'Transaction ID', 'Date', 'Store Code', 'SKU',
      'Quantity', 'Unit Price', 'Total Amount', 'Channel',
    ],
    sampleRow: {
      'Transaction ID': 'TXN-001',
      'Date': '2024-01-15',
      'Store Code': 'STORE-CAS-01',
      'SKU': 'SKU-001',
      'Quantity': 2,
      'Unit Price': 120.00,
      'Total Amount': 240.00,
      'Channel': 'In-Store',
    },
  },
  forecasts: {
    sheetName: 'Forecasts',
    headers: [
      'SKU', 'Store Code', 'Period Start', 'Period End',
      'Forecast Quantity', 'Confidence (%)', 'Method', 'Notes',
    ],
    sampleRow: {
      'SKU': 'SKU-001',
      'Store Code': 'STORE-CAS-01',
      'Period Start': '2024-02-01',
      'Period End': '2024-02-28',
      'Forecast Quantity': 200,
      'Confidence (%)': 85,
      'Method': 'Exponential Smoothing',
      'Notes': '',
    },
  },
  stores: {
    sheetName: 'Stores',
    headers: [
      'Store Code', 'Store Name', 'Country', 'City',
      'Address', 'Type', 'Status', 'Manager',
    ],
    sampleRow: {
      'Store Code': 'STORE-CAS-01',
      'Store Name': 'Morocco Mall',
      'Country': 'Morocco',
      'City': 'Casablanca',
      'Address': 'Morocco Mall, Ground Floor',
      'Type': 'Flagship',
      'Status': 'Active',
      'Manager': 'Karim El Amrani',
    },
  },
};

// ---------------------------------------------------------------------------
// Export to CSV  (ColumnDef API)
// ---------------------------------------------------------------------------

/**
 * Export data array to a CSV file.
 * Uses ColumnDef for column definitions. If columns are not supplied, all
 * object keys from the first row are used as headers.
 */
export const exportToCSV = <T extends Record<string, any>>(
  data: T[],
  filenameOrColumns: string | ExportColumn[],
  columnsOrOptions?: ColumnDef[] | ExportOptions,
): void => {
  // Overload resolution: support both old and new signatures
  let columns: ExportColumn[];
  let options: ExportOptions;

  if (typeof filenameOrColumns === 'string') {
    // New API: exportToCSV(data, filename, columns?)
    const filename = filenameOrColumns;
    const colDefs = columnsOrOptions as ColumnDef[] | undefined;
    columns = colDefs
      ? toExportColumns(colDefs)
      : data.length > 0
        ? Object.keys(data[0]).map((k) => ({ key: k, label: k }))
        : [];
    options = { filename };
  } else {
    // Legacy API: exportToCSV(data, ExportColumn[], ExportOptions)
    columns = filenameOrColumns;
    options = columnsOrOptions as ExportOptions;
  }

  try {
    const transformedData = data.map((row) => {
      const transformedRow: Record<string, any> = {};
      columns.forEach((col) => {
        const value = row[col.key];
        const formatted = col.format ? col.format(value) : value;
        transformedRow[col.label] = typeof formatted === 'string' ? wrapRTL(formatted) : formatted;
      });
      return transformedRow;
    });

    // Add BOM for UTF-8 so Excel correctly recognises Arabic characters
    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    const csv = '\uFEFF' + XLSX.utils.sheet_to_csv(worksheet);
    const filename = `${options.filename}_${new Date().toISOString().split('T')[0]}.csv`;
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename);
  } catch (error) {
    throw new Error('Failed to export data to CSV');
  }
};

// ---------------------------------------------------------------------------
// Export to Excel  (ColumnDef API)
// ---------------------------------------------------------------------------

/**
 * Export data array to an XLSX file.
 * Supports both the new ColumnDef API and the legacy ExportColumn API.
 */
export const exportToExcel = <T extends Record<string, any>>(
  data: T[],
  filenameOrColumns: string | ExportColumn[],
  sheetNameOrOptions?: string | ExportOptions,
  columnsArg?: ColumnDef[],
): void => {
  let columns: ExportColumn[];
  let options: ExportOptions;

  if (typeof filenameOrColumns === 'string') {
    const filename = filenameOrColumns;
    const sheetName = typeof sheetNameOrOptions === 'string' ? sheetNameOrOptions : 'Data';
    columns = columnsArg
      ? toExportColumns(columnsArg)
      : data.length > 0
        ? Object.keys(data[0]).map((k) => ({ key: k, label: k }))
        : [];
    options = { filename, sheetName };
  } else {
    columns = filenameOrColumns;
    options = sheetNameOrOptions as ExportOptions;
  }

  // Use ExcelJS for styled output
  exportToExcelStyled(data, columns, options).catch(() => {
    // Fallback to plain XLSX if ExcelJS fails
    const transformedData = data.map((row) => {
      const transformedRow: Record<string, any> = {};
      columns.forEach((col) => {
        const value = row[col.key];
        const formatted = col.format ? col.format(value) : value;
        transformedRow[col.label] = typeof formatted === 'string' ? wrapRTL(formatted) : formatted;
      });
      return transformedRow;
    });
    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, options.sheetName || 'Data');
    const buf = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${options.filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  });
};

// ---------------------------------------------------------------------------
// Shared style tokens for all branded Excel exports
// ---------------------------------------------------------------------------

const BRAND_COLOR = '722F37';
const HEADER_BG = 'F5F0E8';
const HEADER_FONT = '4A3728';
const ALT_ROW = 'FDFBF7';
const BORDER_COLOR = 'E8E0D4';
const META_FONT = '8A8A85';

const thinBorder: any = { style: 'thin', color: { argb: BORDER_COLOR } };
const cellBorders: any = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

/**
 * Add a presentation-grade data sheet to an existing ExcelJS workbook.
 * Use this from any export path that needs the consistent IRIS styling
 * (branded title bar, meta row, headers, alternating rows, auto-width,
 * frozen header pane, number formatting).
 *
 * The caller is responsible for creating the workbook and writing it
 * to disk — this helper only mutates `wb` by adding one sheet.
 */
export const addStyledDataSheet = (
  wb: any,
  opts: {
    sheetName: string;
    title: string;
    rows: Record<string, any>[];
    /** Optional column defs. If omitted, all keys from the first row are used. */
    columns?: ExportColumn[];
  },
): void => {
  const { sheetName, title, rows } = opts;
  const columns: ExportColumn[] =
    opts.columns ||
    (rows.length > 0 ? Object.keys(rows[0]).map((k) => ({ key: k, label: k })) : []);

  if (columns.length === 0) return;

  const safeName = sheetName.slice(0, 31);
  const ws = wb.addWorksheet(safeName, {
    views: [{ state: 'frozen', ySplit: 3 }],
  });

  const colCount = columns.length;

  // ── Row 1: Title bar ──
  const titleRow = ws.addRow([title]);
  ws.mergeCells(1, 1, 1, colCount);
  const titleCell = titleRow.getCell(1);
  titleCell.font = { bold: true, size: 14, color: { argb: 'FFFFFF' } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_COLOR } };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
  titleRow.height = 32;

  // ── Row 2: Meta ──
  const dateStr = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const metaRow = ws.addRow([`Generated: ${dateStr}  ·  ${rows.length} record${rows.length === 1 ? '' : 's'}`]);
  ws.mergeCells(2, 1, 2, colCount);
  const metaCell = metaRow.getCell(1);
  metaCell.font = { size: 9, italic: true, color: { argb: META_FONT } };
  metaCell.alignment = { horizontal: 'left', vertical: 'middle' };
  metaRow.height = 20;

  // ── Row 3: Column headers ──
  const headerLabels = columns.map((c) => c.label);
  const headerRow = ws.addRow(headerLabels);
  headerRow.height = 24;
  headerRow.eachCell((cell: any) => {
    cell.font = { bold: true, size: 10, color: { argb: HEADER_FONT } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = cellBorders;
  });

  // ── Data rows ──
  rows.forEach((row, rowIdx) => {
    const values = columns.map((col) => {
      const v = row[col.key];
      return col.format ? col.format(v) : v;
    });
    const dataRow = ws.addRow(values);
    dataRow.eachCell((cell: any, colIdx: number) => {
      cell.font = { size: 10 };
      cell.alignment = { vertical: 'middle' };
      cell.border = cellBorders;
      if (rowIdx % 2 === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW } };
      }
      const raw = row[columns[colIdx - 1]?.key];
      if (typeof raw === 'number') {
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
        cell.numFmt = Number.isInteger(raw) ? '#,##0' : '#,##0.00';
      }
    });
  });

  // ── Auto-width columns ──
  ws.columns.forEach((col: any, idx: number) => {
    let maxLen = (headerLabels[idx] || '').length;
    rows.forEach((row) => {
      const v = row[columns[idx]?.key];
      const len = String(v ?? '').length;
      if (len > maxLen) maxLen = len;
    });
    col.width = Math.min(maxLen + 4, 45);
  });
};

/**
 * Add a "Chart" worksheet to an ExcelJS workbook with one or more chart
 * images embedded vertically. Pair with `captureChartsAsPNGs` to grab
 * the images from a DOM container.
 *
 * Layout matches the data sheets: branded title bar (row 1), meta row
 * (row 2), then the images stacked from row 3 down.
 */
export const addChartSheet = (
  wb: any,
  opts: {
    title: string;
    chartImages: Array<{ png: string; width: number; height: number }>;
    chartCaptions?: string[];
  },
): void => {
  const { title, chartImages, chartCaptions = [] } = opts;
  if (chartImages.length === 0) return;

  const sheet = wb.addWorksheet('Charts', {
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 } },
    properties: { defaultColWidth: 12 },
  });

  // Reserve a wide canvas: 16 columns of even width gives charts room to breathe.
  const NUM_COLS = 16;
  for (let c = 1; c <= NUM_COLS; c++) sheet.getColumn(c).width = 12;

  // Title bar
  const titleRow = sheet.addRow([title]);
  sheet.mergeCells(1, 1, 1, NUM_COLS);
  titleRow.getCell(1).font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
  titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_COLOR } };
  titleRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  titleRow.height = 36;

  // Meta
  const dateStr = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const metaRow = sheet.addRow([`Generated: ${dateStr}  ·  ${chartImages.length} chart${chartImages.length === 1 ? '' : 's'}`]);
  sheet.mergeCells(2, 1, 2, NUM_COLS);
  metaRow.getCell(1).font = { size: 9, italic: true, color: { argb: META_FONT } };
  metaRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  metaRow.height = 20;

  // Spacer row
  sheet.addRow([]);
  let nextRow = 4;

  // Each chart gets a caption row + a generously sized image, then spacing.
  // Charts are scaled to fit within ~900x520 px while preserving aspect ratio.
  for (let i = 0; i < chartImages.length; i++) {
    const { png, width, height } = chartImages[i];
    const caption = chartCaptions[i];

    // Optional caption row above the chart image
    if (caption) {
      const capRow = sheet.addRow([caption]);
      sheet.mergeCells(nextRow, 1, nextRow, NUM_COLS);
      capRow.getCell(1).font = { bold: true, size: 11, color: { argb: BRAND_COLOR } };
      capRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
      capRow.height = 22;
      nextRow += 1;
    }

    // Compute presentable size: fit width to ~960px, scale height proportionally
    const MAX_W = 960;
    const MAX_H = 540;
    const ratio = Math.min(MAX_W / (width / 2), MAX_H / (height / 2), 1);
    const displayWidth = Math.round((width / 2) * ratio);
    const displayHeight = Math.round((height / 2) * ratio);

    const imgId = wb.addImage({ base64: png.split(',')[1], extension: 'png' });
    sheet.addImage(imgId, {
      tl: { col: 0.5, row: nextRow - 1 + 0.2 },
      ext: { width: displayWidth, height: displayHeight },
    });

    // Reserve enough rows for the image (≈ 18px per row)
    nextRow += Math.ceil(displayHeight / 18) + 2;

    // Separator between charts (skip after the last)
    if (i < chartImages.length - 1) {
      sheet.addRow([]);
      nextRow += 1;
    }
  }
};

/**
 * Presentation-grade Excel export using ExcelJS — single sheet, styled.
 * Thin wrapper around `addStyledDataSheet`.
 */
async function exportToExcelStyled<T extends Record<string, any>>(
  data: T[],
  columns: ExportColumn[],
  options: ExportOptions,
): Promise<void> {
  const ExcelJS = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'IRIS Platform';
  wb.created = new Date();

  addStyledDataSheet(wb, {
    sheetName: options.sheetName || options.title || 'Data',
    title: options.title || options.filename || 'Export',
    rows: data as Record<string, any>[],
    columns,
  });

  const buf = await wb.xlsx.writeBuffer();
  const filename = `${options.filename}_${new Date().toISOString().split('T')[0]}.xlsx`;
  saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
}

// ---------------------------------------------------------------------------
// Chart capture (Recharts SVG -> PNG)
// ---------------------------------------------------------------------------

/** SVG style props that Recharts often inherits from CSS — must be inlined
 * before serialization, otherwise the standalone SVG renders with default
 * fonts/colors (or invisible text). */
const SVG_STYLE_PROPS = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-anchor',
  'dominant-baseline',
  'visibility',
  'transform',
];

/** Resolve a usable pixel size for an SVG, falling back through:
 *  live layout → bounding rect → width/height attributes → viewBox. */
const resolveSvgSize = (svg: SVGSVGElement): { width: number; height: number } => {
  const rect = svg.getBoundingClientRect();
  let width = svg.clientWidth || rect.width;
  let height = svg.clientHeight || rect.height;

  if (!width || !height) {
    const widthAttr = parseFloat(svg.getAttribute('width') || '0');
    const heightAttr = parseFloat(svg.getAttribute('height') || '0');
    if (widthAttr && heightAttr) {
      width = widthAttr;
      height = heightAttr;
    }
  }

  if (!width || !height) {
    const viewBox = svg.viewBox?.baseVal;
    if (viewBox && viewBox.width && viewBox.height) {
      width = viewBox.width;
      height = viewBox.height;
    }
  }

  // Last-resort sane defaults so a chart never silently disappears.
  if (!width) width = 800;
  if (!height) height = 400;

  return { width: Math.round(width), height: Math.round(height) };
};

/** Walk the source SVG tree and copy critical computed style props onto
 *  the clone. Recharts text would otherwise render in the browser default
 *  font and ignore the page's CSS-variable-driven typography. */
const inlineComputedStyles = (source: SVGSVGElement, clone: SVGSVGElement): void => {
  const sourceEls = source.querySelectorAll<SVGElement>('*');
  const cloneEls = clone.querySelectorAll<SVGElement>('*');
  const len = Math.min(sourceEls.length, cloneEls.length);
  for (let i = 0; i < len; i++) {
    const sEl = sourceEls[i];
    const cEl = cloneEls[i];
    if (!sEl || !cEl || !cEl.style) continue;
    const computed = window.getComputedStyle(sEl);
    for (const prop of SVG_STYLE_PROPS) {
      const value = computed.getPropertyValue(prop);
      if (value && value !== 'none' && value !== 'normal') {
        cEl.style.setProperty(prop, value);
      }
    }
  }
};

/** Render via the modern createImageBitmap API. Most reliable on Chrome /
 *  Firefox / Edge / Safari 17+ — does not depend on Image lifecycle quirks. */
const renderViaImageBitmap = async (
  svgString: string,
  width: number,
  height: number,
  scale: number,
): Promise<string> => {
  if (typeof createImageBitmap !== 'function') {
    throw new Error('createImageBitmap not supported');
  }
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2d context');
    ctx.scale(scale, scale);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    return canvas.toDataURL('image/png');
  } finally {
    bitmap.close?.();
  }
};

/** Fallback: HTMLImageElement + data: URL. Slower than ImageBitmap but
 *  works in older browsers. Wrapped in a hard timeout so it can never
 *  silently hang the export. */
const renderViaImage = (
  svgString: string,
  width: number,
  height: number,
  scale: number,
): Promise<string> => {
  // btoa requires Latin-1 — encode UTF-8 first.
  const svgBase64 = btoa(unescape(encodeURIComponent(svgString)));
  const dataUrl = `data:image/svg+xml;base64,${svgBase64}`;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const timeoutId = window.setTimeout(() => {
      reject(new Error('SVG image load timed out after 10s'));
    }, 10_000);

    img.onload = () => {
      window.clearTimeout(timeoutId);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get 2d context'));
          return;
        }
        ctx.scale(scale, scale);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      window.clearTimeout(timeoutId);
      reject(new Error('SVG image load failed (data URL)'));
    };

    img.src = dataUrl;
  });
};

/**
 * Capture every <svg> chart inside a container as PNG data URLs.
 *
 * Pipeline per chart:
 *   1. Resolve a usable pixel size (multi-fallback).
 *   2. Clone the SVG, set explicit width/height + xmlns.
 *   3. Inline computed styles so Recharts text/colours survive serialization.
 *   4. Try createImageBitmap → canvas → PNG (modern, fastest).
 *   5. Fall back to Image with data URL.
 *
 * Errors are logged with rich context but never throw — one bad chart
 * does not abort the whole export.
 */
export const captureChartsAsPNGs = async (
  container: HTMLElement,
): Promise<Array<{ png: string; width: number; height: number }>> => {
  // Recharts uses its Surface component (class="recharts-surface") for
  // BOTH the main chart SVG and the tiny legend-icon SVGs (14×14 colored
  // markers). We must exclude:
  //  • Legend icons — inside a .recharts-legend-wrapper ancestor
  //  • MUI icons (TrendingUp, etc.) — no recharts-surface class
  // That leaves only the actual chart SVGs.
  const allSvgs = Array.from(container.querySelectorAll('svg'));
  const svgs = allSvgs.filter((svg) =>
    svg.classList.contains('recharts-surface') &&
    !svg.closest('.recharts-legend-wrapper'),
  );
  if (svgs.length === 0) {
    // eslint-disable-next-line no-console
    console.warn('[captureChartsAsPNGs] no chart SVGs inside container (found %d total SVGs, all filtered)', allSvgs.length);
    return [];
  }

  const results: Array<{ png: string; width: number; height: number }> = [];
  const SCALE = 2;

  for (let i = 0; i < svgs.length; i++) {
    const svg = svgs[i];
    try {
      const { width, height } = resolveSvgSize(svg);
      if (!width || !height) {
        // eslint-disable-next-line no-console
        console.warn(`[captureChartsAsPNGs] chart ${i} has zero dimensions, skipping`);
        continue;
      }

      // Clone with explicit dimensions and namespaces
      const clone = svg.cloneNode(true) as SVGSVGElement;
      clone.setAttribute('width', String(width));
      clone.setAttribute('height', String(height));
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

      // Inline computed styles so the standalone SVG renders identically
      inlineComputedStyles(svg, clone);

      const svgString = new XMLSerializer().serializeToString(clone);

      // Try the fastest path first, fall back if it fails
      let png: string | null = null;
      try {
        png = await renderViaImageBitmap(svgString, width, height, SCALE);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[captureChartsAsPNGs] chart ${i}: ImageBitmap failed, trying Image fallback`, err);
      }

      if (!png) {
        try {
          png = await renderViaImage(svgString, width, height, SCALE);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`[captureChartsAsPNGs] chart ${i}: Image fallback failed`, err);
          continue;
        }
      }

      results.push({ png, width: width * SCALE, height: height * SCALE });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[captureChartsAsPNGs] chart ${i}: unexpected error`, err);
    }
  }

  // eslint-disable-next-line no-console
  console.info(`[captureChartsAsPNGs] captured ${results.length} of ${svgs.length} chart(s) (${allSvgs.length - svgs.length} icon SVGs filtered out)`);
  return results;
};

/**
 * Export data + chart(s) to a styled Excel file with a Chart sheet.
 * The chart(s) are captured as PNGs via the native SVG -> Canvas path
 * and embedded in a "Chart" worksheet that precedes the data sheet.
 *
 * Both the chart sheet and the data sheet share the same branded
 * styling as every other IRIS export, via `addChartSheet` and
 * `addStyledDataSheet`.
 */
export const exportWithChart = async <T extends Record<string, any>>(
  data: T[],
  filename: string,
  title: string,
  chartRef: RefObject<HTMLElement>,
  sheetName?: string,
  columns?: ExportColumn[],
): Promise<void> => {
  const ExcelJS = await import('exceljs');

  const wb = new ExcelJS.Workbook();
  wb.creator = 'IRIS Platform';
  wb.created = new Date();

  // ── Sheet 1: Chart(s) ──
  if (chartRef.current) {
    const chartImages = await captureChartsAsPNGs(chartRef.current);
    if (chartImages.length > 0) {
      addChartSheet(wb, { title, chartImages });
    } else {
      // eslint-disable-next-line no-console
      console.warn('exportWithChart: no <svg> charts found inside chartRef container');
    }
  }

  // ── Sheet 2: Data ──
  addStyledDataSheet(wb, {
    sheetName: sheetName || 'Data',
    title,
    rows: data as Record<string, any>[],
    columns,
  });

  const buf = await wb.xlsx.writeBuffer();
  const fName = `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`;
  saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fName);
};

// ---------------------------------------------------------------------------
// Export to PDF  (ColumnDef API)
// ---------------------------------------------------------------------------

/**
 * Export data array to a PDF file, optionally with embedded chart images.
 * Supports both new ColumnDef API and legacy ExportColumn API.
 */
export const exportToPDF = async <T extends Record<string, any>>(
  data: T[],
  filenameOrColumns: string | ExportColumn[],
  titleOrOptions?: string | ExportOptions,
  columnsArg?: ColumnDef[],
  chartRef?: RefObject<HTMLElement>,
): Promise<void> => {
  let columns: ExportColumn[];
  let options: ExportOptions;

  if (typeof filenameOrColumns === 'string') {
    // New API: exportToPDF(data, filename, title?, columns?)
    const filename = filenameOrColumns;
    const title = typeof titleOrOptions === 'string' ? titleOrOptions : undefined;
    columns = columnsArg
      ? toExportColumns(columnsArg)
      : data.length > 0
        ? Object.keys(data[0]).map((k) => ({ key: k, label: k }))
        : [];
    options = { filename, title };
  } else {
    // Legacy API: exportToPDF(data, ExportColumn[], ExportOptions)
    columns = filenameOrColumns;
    options = titleOrOptions as ExportOptions;
  }

  try {
    // Landscape for wide tables
    const isWide = columns.length > 6;
    const doc = new jsPDF({ orientation: isWide ? 'landscape' : 'portrait' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Title
    if (options.title) {
      doc.setFontSize(16);
      doc.text(options.title, 14, 15);
    }

    // Subtitle
    if (options.subtitle) {
      doc.setFontSize(10);
      doc.text(options.subtitle, 14, 22);
    }

    // Generated timestamp
    const now = new Date();
    const timestampY = options.title && options.subtitle ? 28 : options.title ? 22 : 10;
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Generated: ${now.toLocaleDateString('en-US')} ${now.toLocaleTimeString('en-US')}`,
      14,
      timestampY
    );
    doc.setTextColor(0);

    let nextY = timestampY + 4;

    // ── Chart images (one after another on the first page(s)) ──
    if (chartRef?.current) {
      const chartImages = await captureChartsAsPNGs(chartRef.current);
      const margin = 14;
      const maxImgWidth = pageWidth - margin * 2;

      for (const img of chartImages) {
        // Scale chart to fit page width, preserving aspect ratio
        const ratio = Math.min(maxImgWidth / (img.width / 2), 1);
        const displayW = (img.width / 2) * ratio;
        const displayH = (img.height / 2) * ratio;

        // If chart won't fit on current page, add a new page
        if (nextY + displayH > pageHeight - 15) {
          doc.addPage();
          nextY = 10;
        }

        doc.addImage(img.png, 'PNG', margin, nextY, displayW, displayH);
        nextY += displayH + 8;
      }
    }

    // ── Data table ──
    if (data.length > 0) {
      const headers = columns.map((col) => col.label);
      const rows = data.map((row) =>
        columns.map((col) => {
          const value = row[col.key];
          const formatted = col.format ? col.format(value) : String(value ?? '');
          return wrapRTL(formatted);
        })
      );

      // If charts pushed us far down, start the table on a new page
      if (nextY > pageHeight - 60) {
        doc.addPage();
        nextY = 10;
      }

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: nextY,
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [44, 44, 44], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        margin: { top: 10, left: 10, right: 10 },
        columnStyles: columnsArg
          ? Object.fromEntries(
              columnsArg
                .map((c, i) => (c.width ? [i, { cellWidth: c.width }] : null))
                .filter(Boolean) as [number, { cellWidth: number }][]
            )
          : undefined,
      });
    }

    // Footer
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150);
      doc.text(`IRIS - Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 8, {
        align: 'center',
      });
    }

    const filename = `${options.filename}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  } catch (error) {
    throw new Error('Failed to export data to PDF');
  }
};

// ---------------------------------------------------------------------------
// Export Chart/Canvas as Image
// ---------------------------------------------------------------------------

/**
 * Export a chart (or any DOM element) as a PNG or SVG image.
 *
 * For Recharts: wrap the chart in a container and pass a ref to that container.
 * The function looks for an <svg> or <canvas> element inside the ref target.
 *
 * @param chartRef - React ref pointing to the chart container element
 * @param filename - Output filename (without extension)
 * @param format - 'png' (default) or 'svg'
 */
export const exportChartAsImage = async (
  chartRef: RefObject<HTMLElement>,
  filename: string,
  format: 'png' | 'svg' = 'png',
): Promise<void> => {
  const container = chartRef.current;
  if (!container) {
    throw new Error('Chart reference is not available');
  }

  const svgElement = container.querySelector('svg');
  const canvasElement = container.querySelector('canvas');

  if (format === 'svg' && svgElement) {
    // Export raw SVG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const exportFilename = `${filename}_${new Date().toISOString().split('T')[0]}.svg`;
    saveAs(blob, exportFilename);
    return;
  }

  // PNG export via canvas
  if (canvasElement) {
    // Direct canvas element (e.g., Chart.js)
    canvasElement.toBlob((blob) => {
      if (blob) {
        const exportFilename = `${filename}_${new Date().toISOString().split('T')[0]}.png`;
        saveAs(blob, exportFilename);
      }
    }, 'image/png');
    return;
  }

  if (svgElement) {
    // Convert SVG to PNG via offscreen canvas
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      // Use 2x scale for crisp exports
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = svgElement.clientWidth * scale;
      canvas.height = svgElement.clientHeight * scale;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const exportFilename = `${filename}_${new Date().toISOString().split('T')[0]}.png`;
            saveAs(blob, exportFilename);
          }
          URL.revokeObjectURL(url);
        }, 'image/png');
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      throw new Error('Failed to render chart as image');
    };
    img.src = url;
    return;
  }

  // Fallback: use html2canvas-like approach with a simple canvas screenshot
  // This handles cases where the container has neither SVG nor canvas
  throw new Error('No SVG or canvas element found in the chart container');
};

// ---------------------------------------------------------------------------
// PowerPoint Export
// ---------------------------------------------------------------------------

/**
 * Build a presentation-grade PowerPoint deck from a data table and an
 * optional chart container. The deck has:
 *
 *  • Slide 1: Title slide (branded)
 *  • Slide 2..N: One slide per captured chart
 *  • Slide N+1: Data table (paginated across slides if it doesn't fit)
 *
 * Charts are captured from the DOM via `captureChartsAsPNGs`. Pass a ref
 * to a container that wraps the chart(s) you want included.
 */
export const exportToPPTX = async <T extends Record<string, any>>(
  data: T[],
  filename: string,
  title: string,
  options?: {
    subtitle?: string;
    columns?: ExportColumn[];
    chartRef?: RefObject<HTMLElement>;
  },
): Promise<void> => {
  // Lazy-load to keep the main bundle small
  const PptxGenJS = (await import('pptxgenjs')).default;
  const pptx = new PptxGenJS();

  pptx.author = 'IRIS Platform';
  pptx.title = title;
  pptx.layout = 'LAYOUT_WIDE'; // 13.33 x 7.5 in

  const BRAND_HEX = '8B7355';     // muted gold/taupe — matches IRIS brand
  const BRAND_DARK = '5C4A36';
  const TEXT_DARK = '2C2C2C';
  const TEXT_MUTED = '777777';

  // ---- Slide 1: Title ----
  const titleSlide = pptx.addSlide();
  titleSlide.background = { color: 'FFFFFF' };
  // Top accent bar
  titleSlide.addShape(pptx.ShapeType.rect, {
    x: 0, y: 0, w: 13.33, h: 0.6,
    fill: { color: BRAND_HEX }, line: { color: BRAND_HEX, width: 0 },
  });
  titleSlide.addText('IRIS PLATFORM', {
    x: 0.5, y: 0.13, w: 12, h: 0.35,
    fontSize: 12, bold: true, color: 'FFFFFF', fontFace: 'Calibri', charSpacing: 4,
  });
  titleSlide.addText(title, {
    x: 0.7, y: 2.3, w: 12, h: 1.3,
    fontSize: 36, bold: true, color: TEXT_DARK, fontFace: 'Calibri',
  });
  if (options?.subtitle) {
    titleSlide.addText(options.subtitle, {
      x: 0.7, y: 3.6, w: 12, h: 0.6,
      fontSize: 18, color: TEXT_MUTED, fontFace: 'Calibri',
    });
  }
  titleSlide.addText(
    `Generated ${new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`,
    { x: 0.7, y: 6.6, w: 12, h: 0.4, fontSize: 11, italic: true, color: TEXT_MUTED, fontFace: 'Calibri' }
  );

  // ---- Helper: branded header for content slides ----
  const addContentHeader = (slide: any, text: string) => {
    slide.background = { color: 'FFFFFF' };
    slide.addShape(pptx.ShapeType.rect, {
      x: 0, y: 0, w: 13.33, h: 0.55,
      fill: { color: BRAND_HEX }, line: { color: BRAND_HEX, width: 0 },
    });
    slide.addText(text, {
      x: 0.4, y: 0.08, w: 12.5, h: 0.4,
      fontSize: 16, bold: true, color: 'FFFFFF', fontFace: 'Calibri',
    });
    // Footer rule
    slide.addShape(pptx.ShapeType.line, {
      x: 0.4, y: 7.15, w: 12.53, h: 0,
      line: { color: 'DDDDDD', width: 0.75 },
    });
    slide.addText('IRIS Platform', {
      x: 0.4, y: 7.18, w: 12.5, h: 0.25,
      fontSize: 9, italic: true, color: TEXT_MUTED, fontFace: 'Calibri',
    });
  };

  // ---- Slides 2..N: Charts ----
  if (options?.chartRef?.current) {
    const chartImages = await captureChartsAsPNGs(options.chartRef.current);
    chartImages.forEach((img, idx) => {
      const slide = pptx.addSlide();
      addContentHeader(slide, chartImages.length > 1 ? `${title} — Chart ${idx + 1}` : `${title} — Chart`);

      // Fit chart preserving aspect ratio inside an 11.5 x 5.7 in box
      const MAX_W = 11.5;
      const MAX_H = 5.7;
      const ratio = Math.min(MAX_W / (img.width / 96), MAX_H / (img.height / 96), 1.5);
      const w = (img.width / 96) * ratio;
      const h = (img.height / 96) * ratio;
      const x = (13.33 - w) / 2;
      const y = 0.85 + (MAX_H - h) / 2;

      slide.addImage({ data: img.png, x, y, w, h });
    });
  }

  // ---- Slide N+1..: Data table (paginated) ----
  if (data.length > 0) {
    const cols: ExportColumn[] = options?.columns || (
      Object.keys(data[0]).map((k) => ({ key: k, label: k }))
    );

    const headers = cols.map((c) => ({
      text: c.label,
      options: { bold: true, color: 'FFFFFF', fill: { color: BRAND_DARK }, align: 'left' as const },
    }));

    const allRows = data.map((row) =>
      cols.map((c) => ({
        text: c.format ? c.format(row[c.key]) : String(row[c.key] ?? ''),
        options: { color: TEXT_DARK, align: 'left' as const, fontSize: 9 },
      }))
    );

    // Approx 18 data rows per slide at 11pt — paginate to keep tables readable
    const ROWS_PER_SLIDE = 18;
    const totalPages = Math.max(1, Math.ceil(allRows.length / ROWS_PER_SLIDE));

    for (let p = 0; p < totalPages; p++) {
      const slide = pptx.addSlide();
      addContentHeader(slide, totalPages > 1 ? `${title} — Data (${p + 1}/${totalPages})` : `${title} — Data`);

      const slice = allRows.slice(p * ROWS_PER_SLIDE, (p + 1) * ROWS_PER_SLIDE);
      const tableRows = [headers, ...slice];

      slide.addTable(tableRows as any, {
        x: 0.4, y: 0.85, w: 12.5, h: 6.0,
        fontFace: 'Calibri',
        fontSize: 10,
        border: { type: 'solid', pt: 0.5, color: 'E0E0E0' },
        rowH: 0.3,
        autoPage: false,
      });
    }
  }

  await pptx.writeFile({ fileName: `${filename}_${new Date().toISOString().split('T')[0]}.pptx` });
};

// ---------------------------------------------------------------------------
// Download Template
// ---------------------------------------------------------------------------

/**
 * Download an Excel template for bulk data imports.
 * @param templateType - One of: inventory, products, sales, forecasts, stores
 */
export const downloadTemplate = (templateType: string): void => {
  const definition = templateDefinitions[templateType];

  if (!definition) {
    throw new Error(`Template type "${templateType}" is not available. Valid types: ${Object.keys(templateDefinitions).join(', ')}`);
  }

  try {
    // Build header row + optional sample row
    const sheetData: Record<string, any>[] = [];

    if (definition.sampleRow) {
      sheetData.push(definition.sampleRow);
    }

    // If no sample row, create an empty row so the headers still appear
    if (sheetData.length === 0) {
      const emptyRow: Record<string, any> = {};
      definition.headers.forEach((h) => (emptyRow[h] = ''));
      sheetData.push(emptyRow);
    }

    const worksheet = XLSX.utils.json_to_sheet(sheetData, {
      header: definition.headers,
    });

    // Set column widths based on header lengths
    worksheet['!cols'] = definition.headers.map((h) => ({
      wch: Math.max(h.length + 4, 15),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, definition.sheetName);

    const buf = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const filename = `IRIS_${templateType}_template.xlsx`;
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), filename);
  } catch (error) {
    throw new Error(`Failed to create ${templateType} template`);
  }
};

/**
 * Get the list of available template types.
 */
export const getAvailableTemplates = (): { type: string; label: string }[] => {
  return Object.entries(templateDefinitions).map(([type, def]) => ({
    type,
    label: def.sheetName,
  }));
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export const formatDateForExport = (date: string | Date): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

export const formatCurrencyForExport = (value: number, currency: string = 'MAD'): string => {
  if (value === null || value === undefined) return `${currency} 0.00`;
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (currency === 'EUR') return `${formatted} €`;
  if (currency === 'USD') return `$${formatted}`;
  return `${currency} ${formatted}`;
};

export const formatPercentageForExport = (value: number): string => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(2)}%`;
};

export const formatNumberForExport = (value: number, decimals: number = 0): string => {
  if (value === null || value === undefined) return '0';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format Arabic date string (for MENA region exports).
 */
export const formatArabicDate = (date: string | Date): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('fr-MA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Helper to auto-generate ColumnDef[] from a data array.
 * Inspects the first row to derive keys and human-readable headers.
 */
export const autoColumns = (data: Record<string, any>[]): ColumnDef[] => {
  if (!data || data.length === 0) return [];
  return Object.keys(data[0]).map((key) => ({
    key,
    header: key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()),
  }));
};
